import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { selectActivePage } from '../../../store/ui';

const EmptyDot = (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
    <circle cx="4" cy="4" r="4" fill="#D9D9D9"/>
  </svg>
);

const ActiveDot = (
	<svg width="8" height="8" viewBox="0 0 8 8" fill="none">
    <circle cx="4" cy="4" r="4" fill="#3393EC"/>
  </svg>
);

const SliderWrapper = styled.div`
	width: 114.5%;
	min-height: 250px;
  margin-left: ${({isShift}) => isShift ? '-24px' : '-8px'};
	position: relative;
	overflow-x: hidden;
	overflow-y: visible;
`;

const SliderList = styled.ul`
	display: flex;
	width: 100%;
	height: 100%;
	list-style: none;
	padding: 0;
	margin: 0;
`;

const ItemSlide = styled.li`
	flex-shrink: 0;
	max-width: 327px;
	height: 100%;
  &:first-child {
    margin-left: 24px;
  }
`;

const DotsContainer = styled.div`
	display: flex;
	justify-content: center;
	@media screen and (max-width: 450px) {
		${({isPaddingBottom}) => isPaddingBottom && css`
			padding-bottom: 50px;
		`}
	}
`;

const CommentItem = styled.div`
  flex: 1;
  margin: 30px 6px;
  padding: 24px;
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  border-radius: 32px;
  background: ${(props) => props.theme.colors.colorWhite};
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.10);
`

const StarsImg = styled.div`
  background-image: url('../assets/extended/stars.png');
  width: 131px;
  height: 22px;
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
`

const Reviewer = styled.h3`
  text-align: left;
  font-weight: ${(props) => props.theme.fontWeights.bold};
	font-size: ${(props) => props.theme.fontSizes.font14};
	line-height: ${(props) => props.theme.lineHeights.lineHeight22};
	color: ${(props) => props.theme.colors.colorBlack};
`

const Text = styled(Reviewer)`
  font-weight: ${(props) => props.theme.fontWeights.extraLight};
`

const Dot = styled.div`
	&:not(:last-child) {
		margin-right: 5px;
	}
`;

const ReviewSlider = ({ slideData }) => {
	const containerRef = useRef(null);
	const intervalRef = useRef(null);
	const [current, setCurrent] = useState(1);
	const [translateX, setTranslateX] = useState(0);
	const activePage = useSelector(selectActivePage);
	const handleTouchStart = (e) => {
		const startX = e.touches[0].clientX;
		let deltaX = 0;

		const handleTouchMove = (e) => {
			deltaX = e.touches[0].clientX - startX;
		};

		const handleTouchEnd = () => {
			if (deltaX !== 0) {
				const sensitivity = 50;
				if (deltaX > sensitivity) {
					handleSwipe('prev');
				} else if (deltaX < -sensitivity) {
					handleSwipe('next');
				}
			}

			deltaX = 0;
			document.removeEventListener('touchmove', handleTouchMove);
			document.removeEventListener('touchend', handleTouchEnd);
		};

		document.addEventListener('touchmove', handleTouchMove);
		document.addEventListener('touchend', handleTouchEnd);
	};

	const SlideItem = ({ id, reviewer, text }) => {
		return (
			<ItemSlide>
				<CommentItem key={id}>
          <StarsImg />
          <Reviewer>{reviewer}</Reviewer>
          <Text>{text}</Text>
        </CommentItem>
			</ItemSlide>
		);
	};

	const slides = useMemo(() => {
		if (slideData.length > 1) {
			let items = slideData.map((data, index) => (
				<SlideItem
					key={data.id}
					reviewer={data.reviewer}
					text={data.text}
				/>
			));
			return [
				<SlideItem
					key={slideData.length + 1}
					reviewer={slideData[slideData.length - 1].reviewer}
					text={slideData[slideData.length - 1].text}
				></SlideItem>,
				...items,
				<SlideItem
					key={slideData.length + 2}
					reviewer={slideData[0].reviewer}
					text={slideData[0].text}
				></SlideItem>,
			];
		}
		return (
			<SlideItem
				key={slideData.length + 2}
				reviewer={slideData[0].reviewer}
				text={slideData[0].text}
			></SlideItem>
		);
	}, [slideData]);

	const handleSwipe = useCallback(
		(mode) => {
			containerRef.current.style.transitionDuration = '400ms';
			if (mode === 'prev') {
				if (current <= 1) {
					setTranslateX(0);
					setCurrent(slideData.length);
				} else {
					setTranslateX((containerRef.current.clientWidth - 48) * (current - 1));
					setCurrent((prev) => --prev);
				}
			} else if (mode === 'next') {
				if (current >= slideData.length) {
					setTranslateX(
						(containerRef.current.clientWidth - 48) * (slideData.length + 1)
					);
					setCurrent(1);
				} else {
					setTranslateX((containerRef.current.clientWidth - 48) * (current + 1));
					setCurrent((prev) => ++prev);
				}
			}
		},
		[current, slideData]
	);

	useEffect(() => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
		}
		intervalRef.current = setInterval(() => {
			handleSwipe('next');
		}, 3000);

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [handleSwipe]);

	useLayoutEffect(() => {
		setTranslateX((containerRef.current.clientWidth - 48) * current);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const transitionEnd = () => {
			if (current <= 1) {
				containerRef.current.style.transitionDuration = '0ms';
				setTranslateX((containerRef.current.clientWidth - 48) * current);
			}
			if (current >= slideData.length) {
				containerRef.current.style.transitionDuration = '0ms';
				setTranslateX((containerRef.current.clientWidth - 48) * slideData.length);
			}
		};
		document.addEventListener('transitionend', transitionEnd);
		return () => {
			document.removeEventListener('transitionend', transitionEnd);
		};
	}, [current, slideData]);
	return (
		<>
			<SliderWrapper isShift={activePage === 'checkout'}>
				<SliderList
					ref={containerRef}
					style={{
						transform: `translate3d(${-translateX}px, 0, 0)`,
					}}
					onTouchStart={handleTouchStart}
				>
					{slides}
				</SliderList>
			</SliderWrapper>
			<DotsContainer isPaddingBottom={activePage === 'looking-location'}>
				{slides.map((_, slideIndex) => {
					if (slideIndex > 0 && slideIndex < slides.length - 1) {
						return (
							<Dot key={slideIndex}>
								{slideIndex === current ? ActiveDot : EmptyDot}
							</Dot>
						);
					} else return null;
				})}
			</DotsContainer>
		</>
	);
};

export default ReviewSlider;
